/* eslint-disable no-use-before-define */
import { useState } from "react"
import type { Dispatch, SetStateAction } from "react"

import { AxiosError } from "axios"
import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isNumber from "lodash-es/isNumber"
import { useUnmount, useUpdateEffect } from "react-use"

import { generateUuid, isPreconditionRequiredError, reduxBatch, safeTransition } from "@/3514/utils"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import {
    type TCoachingChatCreateConversationMutation as TCreateConversationMutation,
    type TCoachingChatCreateConversationMutationResponse as TCreateConversationMutationResponse,
    useCoachingChatCreateConversationMutation as useCreateConversationMutation,
    useCoachingChatSocketEmitter as useSocketEmitter,
    useCoachingChatSocketReceiver as useSocketReceiver
} from "../api"
import { useCoachingChatContext as useChatContext } from "../context"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatFormDispatcher as useFormDispatcher,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import {
    ECoachingChatMessageAuthor as EChatMessageAuthor,
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatSocketEmitterEventName as ESockerEmitterEventName,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatSocketStartConversationEventPayload as TStartConversationEventPayload,
    type TCoachingChatSocketStartConversationEventResponse as TStartConversationEventResponse
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

import { useCoachingChatSocketErrorConversationStarterListener as useSocketErrorListener } from "./coaching-chat.socket-errors.hooks"

const { adaptSummaryDtosToStoreModels: getSummaryCards } = uiUtils

type TStarter = {
    startConversation: TEmptyAsyncCallback
    isLoading: boolean
    isSuccess: boolean
    isError: boolean
    failureCount: number
}

type _TSocketTransaction = Omit<TStarter, "startConversation">

function useStarter(): ReturnType<() => TStarter> {
    const {
        entityId,
        chapterId,
        guide: { handleModalOpen: handleGuideModalOpen }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const formDispatcher: ReturnType<typeof useFormDispatcher> = useFormDispatcher(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const { error: bannerError }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const isSocketMode: boolean = eq(conversationApiMode, EConversationApiMode.Sockets)

    const restMutation: TCreateConversationMutation = useCreateConversationMutation({
        chapterComponentId: entityId,
        chapterId
    })

    const socketEmitter: ReturnType<typeof useSocketEmitter<TStartConversationEventPayload>> =
        useSocketEmitter<TStartConversationEventPayload>(ESockerEmitterEventName.StartConversation)

    const [socketTransaction, setSocketTransaction]: [
        _TSocketTransaction,
        Dispatch<SetStateAction<_TSocketTransaction>>
    ] = useState<_TSocketTransaction>({
        isError: false,
        isSuccess: false,
        isLoading: false,
        failureCount: 0
    })

    useUpdateEffect((): void => {
        if (isNull(bannerError))
            safeTransition((): void =>
                setSocketTransaction({ isLoading: false, isError: false, isSuccess: false, failureCount: 0 })
            )
    }, [bannerError])

    function handleError(): void {
        handleGuideModalOpen({
            onSubmit: async (): Promise<void> => {
                await startConversation()
            }
        })
    }

    async function startConversation(): Promise<void> {
        if (!isSocketMode)
            return void restMutation.mutateAsync(void 0, {
                onSuccess: async ({ data: responseData }: TCreateConversationMutationResponse): Promise<void> => {
                    if (!isEmpty(responseData)) {
                        reduxBatch((): void => {
                            formDispatcher.submitForm()
                            conversationDispatcher.loadConversation(
                                {
                                    id: responseData.id,
                                    summary: getSummaryCards(responseData.summary_cards),
                                    status: null,
                                    completedAt: responseData.completed_stamp,
                                    isMatrixFeedbackCompleted: responseData.is_matrix_feedback_completed,
                                    messages: [
                                        {
                                            id: 1,
                                            conversationId: responseData.id,
                                            content: String(""),
                                            author: EChatMessageAuthor.AI,
                                            clientMessageId: generateUuid(),
                                            isStreamable: false,
                                            status: EConversationMessagePhase.Waiting
                                        }
                                    ]
                                },
                                EConversationStatus.InProgress
                            )
                        })

                        setConversationApiMode(EConversationApiMode.Rest)
                    }
                },
                onError: async (error: AxiosError): Promise<void> => {
                    if (isPreconditionRequiredError(error)) handleError()
                }
            })

        if (isSocketMode)
            return void socketEmitter.emit(
                {
                    chapter_progress: chapterId,
                    component: entityId,
                    auto_stream: true
                },
                {
                    onSettled: (): void =>
                        void safeTransition((): void => {
                            setSocketTransaction(
                                (s: _TSocketTransaction): _TSocketTransaction => ({
                                    ...s,
                                    isSuccess: false,
                                    isError: false,
                                    isLoading: true
                                })
                            )
                        })
                }
            )

        return void 0
    }

    useSocketErrorListener({
        handleError: (): void =>
            safeTransition((): void => {
                conversationDispatcher.changeStatus(EConversationStatus.ReadyToStart)
                setSocketTransaction(
                    (s: _TSocketTransaction): _TSocketTransaction => ({
                        ...s,
                        isLoading: false,
                        isError: true,
                        failureCount: s.failureCount + 1
                    })
                )
                handleError()
            })
    })

    useSocketReceiver<TStartConversationEventResponse>({
        isEnabled: isSocketMode,
        event: ESocketListenerEventName.StartConversation,
        handler: (data: TStartConversationEventResponse): void => {
            if (isNumber(data?.conversation_id)) {
                safeTransition((): void =>
                    setSocketTransaction(
                        (s: _TSocketTransaction): _TSocketTransaction => ({ ...s, isLoading: false, isSuccess: true })
                    )
                )

                reduxBatch((): void => {
                    formDispatcher.submitForm()
                    conversationDispatcher.loadConversation(
                        {
                            completedAt: undefined,
                            id: data.conversation_id,
                            status: null,
                            isMatrixFeedbackCompleted: false,
                            summary: [],
                            messages: [
                                {
                                    id: 1,
                                    conversationId: data.conversation_id,
                                    content: String(""),
                                    author: EChatMessageAuthor.AI,
                                    clientMessageId: generateUuid(),
                                    isStreamable: true,
                                    status: EConversationMessagePhase.Waiting
                                }
                            ]
                        },
                        EConversationStatus.Created
                    )
                })

                setConversationApiMode(EConversationApiMode.Sockets)
            }
        }
    })

    useUnmount((): void => {
        setSocketTransaction({ isLoading: false, isError: false, isSuccess: false, failureCount: 0 })
        restMutation.reset()
    })

    return {
        startConversation,
        isLoading: isSocketMode ? socketEmitter.isLoading || socketTransaction.isLoading : restMutation.isPending,
        isSuccess: isSocketMode ? socketEmitter.isSuccess && socketTransaction.isSuccess : restMutation.isSuccess,
        isError: isSocketMode ? socketEmitter.isError || socketTransaction.isError : restMutation.isError,
        failureCount: isSocketMode ? socketTransaction.failureCount : restMutation.failureCount
    }
}

export { useStarter as useCoachingChatConversationStarter }
export type { TStarter as TCoachingChatConversationStarter }
